import React, { useState, useEffect } from 'react';
import { Container } from 'react-bootstrap';
import AppNavbar from './components/AppNavbar';
import apiService from './services/api';
import Login from './components/Login';
import Dashboard from './components/dashboard/Dashboard';
import Organizations from './components/Organizations';
import Subscriptions from './components/Subscriptions';
import PlatformUsers from './components/PlatformUsers';
import EnterpriseUsers from './components/EnterpriseUsers';
import EnterpriseRoles from './components/EnterpriseRoles';
import EnterpriseTeams from './components/EnterpriseTeams';
import Billing from './components/Billing';
import Offerings from './components/Offerings';
import Currencies from './components/Currencies';
import Countries from './components/Countries';
import BillingCycles from './components/BillingCycles';
import FiscalEntities from './components/FiscalEntities';
import PlatformLoginAudit from './components/PlatformLoginAudit';
import EnterpriseLoginAudit from './components/EnterpriseLoginAudit';
import Impersonations from './components/Impersonations';
import EnterpriseUserProfile from './components/EnterpriseUserProfile';
import PlatformRoles from './components/PlatformRoles';
import Solutions from './components/Solutions';
import PasswordResetForm from './components/PasswordResetForm';
import UserSettings from './components/UserSettings';
import NotificationSettings from './components/NotificationSettings';
import SecuritySettings from './components/SecuritySettings';
import PlatformAccessRightsDefaults from './components/PlatformAccessRightsDefaults';
import ProductDataDefaults from './components/ProductDataDefaults';
import StorageLocationTypesDefaults from './components/StorageLocationTypesDefaults';
import SaleDeletionReasonsDefaults from './components/SaleDeletionReasonsDefaults';
import SaleTagValuesDefaults from './components/SaleTagValuesDefaults';
import ReconciliationReportTypesDefaults from './components/ReconciliationReportTypesDefaults';
import UtilizationStats from './components/UtilizationStats';
import DataQuality from './components/DataQuality';
import PlatformAccessRightsManagement from './components/PlatformAccessRightsManagement';
import EnterpriseAccessRightsManagement from './components/EnterpriseAccessRightsManagement';
import SystemMetadata from './components/SystemMetadata';
import InfrastructureStatus from './components/technology/InfrastructureStatus';
import ArchivedRecords from './components/customerdata/ArchivedRecords';
import AuditTrailMetadata from './components/customerdata/AuditTrailMetadata';
import AuditTrailMasterData from './components/customerdata/AuditTrailMasterData';
import DocumentationViewer from './components/DocumentationViewer';
import DocumentationService from './components/DocumentationService';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import useTokenExpiry from './hooks/useTokenExpiry';
import useVersionCheck from './hooks/useVersionCheck';
import TokenExpiryWarningModal from './components/TokenExpiryWarningModal';
import AppUpdateModal from './components/AppUpdateModal';

// Import version test utilities for development - commented out to prevent issues
// if (process.env.NODE_ENV === 'development') {
//   import('./utils/versionTestUtils.js').then((module) => {
//     window.versionTests = module;
//   });
// }

// Main App Component that uses Auth Context
function AppContent() {
  const { user, isAuthenticated, loading, passwordChangeRequired, handlePasswordChanged, logout } = useAuth();
  const [currentView, setCurrentView] = useState('dashboard');

  // Token expiry monitoring
  const {
    isWarningVisible,
    timeUntilExpiry,
    formatTimeLeft,
    dismissWarning,
    extendSession
  } = useTokenExpiry();

  // Version checking - only enable for authenticated users, disabled on mount to prevent login issues
  const {
    showUpdateModal,
    dismissUpdateModal,
    updateNow,
    currentVersion,
    serverVersion,
    isOutdated,
    isChecking,
    lastCheckTime
  } = useVersionCheck({
    enableAutoCheck: isAuthenticated && !passwordChangeRequired,
    checkOnMount: false, // Disable initial check to prevent blocking login
    onUpdateAvailable: (result) => {
      console.log('App update available:', result);
    },
    onError: (error) => {
      console.error('Version check error:', error);
    }
  });



  if (process.env.REACT_APP_DEBUG_GENERAL_INFORMATION === 'true') {
    console.log('=== APP CONTENT COMPONENT ===');
    console.log('Auth state:', { isAuthenticated, passwordChangeRequired, user: user?.user_name });
  }

  const handleLogout = async () => {
    // console.log('=== HANDLE LOGOUT ===');
    // console.log('Logging out user...');
    setCurrentView('dashboard');
    await logout(); // Call the logout function from AuthContext
    // console.log('=== END HANDLE LOGOUT ===');
  };

  const renderCurrentView = () => {
    switch (currentView) {
      case 'dashboard':
        return <Dashboard />;
      case 'organizations':
        return <Organizations />;
      case 'subscriptions':
        return <Subscriptions />;
      case 'platform_users':
        return <PlatformUsers />;
      case 'platform_roles':
        return <PlatformRoles />;
      case 'enterprise_users':
        return <EnterpriseUsers />;
      case 'enterprise_roles':
        return <EnterpriseRoles />;
      case 'enterprise_teams':
        return <EnterpriseTeams />;
      case 'billing':
        return <Billing />;
      case 'offerings':
        return <Offerings />;
      case 'currencies':
        return <Currencies />;
      case 'billing_cycles':
        return <BillingCycles />;
      case 'countries':
        return <Countries />;
      case 'fiscal_entities':
        return <FiscalEntities />;
      case 'platform_login_audit':
        return <PlatformLoginAudit />;
      case 'enterprise_login_audit':
        return <EnterpriseLoginAudit />;
      case 'impersonations':
        return <Impersonations />;
      case 'profile':
        return <EnterpriseUserProfile user={user} />;
      case 'solutions':
        return <Solutions />;
      case 'user_settings':
        return <UserSettings />;
      case 'notification_settings':
        return <NotificationSettings />;
      case 'security_settings':
        return <SecuritySettings />;
      case 'platform_access_rights_defaults':
        return <PlatformAccessRightsDefaults />;
      case 'product_data_defaults':
        return <ProductDataDefaults />;
      case 'storage_location_types_defaults':
        return <StorageLocationTypesDefaults />;
      case 'sale_deletion_reasons_defaults':
        return <SaleDeletionReasonsDefaults />;
      case 'sale_tag_values_defaults':
        return <SaleTagValuesDefaults />;
      case 'reconciliation_report_types_defaults':
        return <ReconciliationReportTypesDefaults />;
      case 'utilization_stats':
        return <UtilizationStats />;
      case 'data_quality':
        return <DataQuality />;
      case 'platform_access_right_management':
        return <PlatformAccessRightsManagement />;
      case 'enterprise_access_right_management':
        return <EnterpriseAccessRightsManagement />;
      case 'system_metadata':
        return <SystemMetadata />;
      case 'infrastructure_status':
        return <InfrastructureStatus />;
      case 'archived_records':
        return <ArchivedRecords />;
      case 'audit_trail_metadata':
        return <AuditTrailMetadata />;
      case 'audit_trail_master_data':
        return <AuditTrailMasterData />;
      case 'documentation_manual':
        return (
          <DocumentationViewer
            src={`${process.env.PUBLIC_URL}/documentation/SIMplify_EDMS_Manual.html`}
            title="SIMplify EDMS Management Training Manual"
          />
        );
      case 'documentation_runbook':
        return (
          <DocumentationViewer
            src={`${process.env.PUBLIC_URL}/documentation/SIMplify_EDMS_RunBook.html`}
            title="SIMplify EDMS Implementation Run Book"
          />
        );
      case 'documentation_comprehensive':
        return <DocumentationService type="comprehensive" />;
      case 'documentation_product_information':
        return <DocumentationService type="product_information" />;
      case 'documentation_prd':
        return <DocumentationService type="prd" />;
      case 'documentation_platform':
        return <DocumentationService type="platform_help_center" />;
      case 'documentation_enterprise':
        return <DocumentationService type="enterprise_help_center" />;
      case 'documentation_implementation_guide':
        return <DocumentationService type="implementation_guide" />;
      case 'documentation_customer_support':
        return <DocumentationService type="customer_support" />;
      case 'documentation_legal_compliance':
        return <DocumentationService type="legal_and_compliance" />;
      case 'documentation_release_notes':
        return <DocumentationService type="release_notes" />;
      case 'documentation_technical':
        return <DocumentationService type="technical_information" />;
      case 'documentation_api_reference':
        return (
          <DocumentationService
            type={null}
            path="docs/swagger"
            title="API Reference"
          />
        );
      default:
        return <Dashboard />;
    }
  };

  // Show loading while checking authentication
  if (loading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center">
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-muted">Loading application...</p>
        </div>
      </div>
    );
  }

  // If not authenticated, show login page
  if (!isAuthenticated) {
    if (process.env.REACT_APP_DEBUG_AUTH_INFORMATION === 'true') {
      console.log('User not authenticated, rendering Login component');
    }
    return <Login />;
  }

  // If authenticated but password change is required, show password reset form
  if (passwordChangeRequired) {
    if (process.env.REACT_APP_DEBUG_AUTH_INFORMATION === 'true') {
      console.log('Password change required, rendering PasswordResetForm');
    }
    return (
      <PasswordResetForm
        user={user}
        onPasswordChanged={handlePasswordChanged}
      />
    );
  }

  if (process.env.REACT_APP_DEBUG_GENERAL_INFORMATION === 'true') {
    console.log('User authenticated, rendering main app');
    console.log('Current view:', currentView);
  }

  return (
    <div className="App">
      {/* Navigation */}
      <AppNavbar
        currentView={currentView}
        setCurrentView={setCurrentView}
        user={user}
        handleLogout={handleLogout}
        versionInfo={{
          isOutdated,
          isChecking,
          lastCheckTime,
          onVersionClick: () => {
            if (isOutdated) {
              // Show update modal when version indicator is clicked and update is available
              // The modal is already controlled by showUpdateModal state
            }
          }
        }}
      />

      {/* Main Content */}
      <div className="main-content">
        <Container fluid className="content-wrapper">
          {renderCurrentView()}
        </Container>
      </div>

      {/* Token Expiry Warning Modal */}
      <TokenExpiryWarningModal
        show={isWarningVisible}
        onHide={dismissWarning}
        timeUntilExpiry={timeUntilExpiry}
        formatTimeLeft={formatTimeLeft}
        onExtendSession={extendSession}
        onLogout={handleLogout}
      />

      {/* App Update Modal */}
      <AppUpdateModal
        show={showUpdateModal}
        onHide={dismissUpdateModal}
        currentVersion={currentVersion}
        serverVersion={serverVersion}
        onUpdateNow={updateNow}
        onLater={dismissUpdateModal}
      />
    </div>
  );
}

// Main App wrapper with AuthProvider
function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;